import React, { useState } from 'react';
import { X, AlertTriangle, Send } from 'lucide-react';
import { useUserManagementStore } from '../../store/userManagementStore';

interface ReportUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
  username: string;
}

const ReportUserModal: React.FC<ReportUserModalProps> = ({
  isOpen,
  onClose,
  userId,
  username
}) => {
  const { reportUser, isSubmittingReport, reportError, clearErrors } = useUserManagementStore();
  
  const [notes, setNotes] = useState('');
  
  if (!isOpen) return null;

  const handleClose = () => {
    clearErrors();
    setNotes('');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!notes.trim()) {
      return;
    }

    try {
      await reportUser({
        reportedId: userId,
        notes: notes.trim()
      });
      
      // Close modal and show success feedback
      handleClose();
      
      // You might want to show a toast notification here
      
    } catch (error) {
      // Error is handled by the store
      console.error('Failed to submit report:', error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-surface border border-white/[.08] rounded-card shadow-panel w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/[.08]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-neon-pink/[.12] rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-neon-pink-soft" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-fg-bright">
                Gebruiker rapporteren
              </h2>
              <p className="text-sm text-fg-muted">
                {username}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white/[.06] rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-fg-faint" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Description */}
          <div className="mb-6">
            <label htmlFor="notes" className="block text-sm font-semibold text-fg-soft mb-2">
              Notities *
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Beschrijf het probleem en wat er gebeurd is..."
              rows={4}
              className="sf-input resize-none"
              maxLength={2000}
            />
            <div className="flex justify-between text-xs text-fg-muted mt-1">
              <span>Leg uit wat er gebeurd is</span>
              <span className="font-mono text-[11px] text-fg-meta">{notes.length}/2000</span>
            </div>
          </div>

          {/* Error message */}
          {reportError && (
            <div className="mb-6 p-3 bg-neon-pink/[.08] border border-neon-pink/30 rounded-field">
              <p className="text-sm text-neon-pink-soft">{reportError}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="sf-btn-secondary flex-1 px-4 py-2"
            >
              Annuleren
            </button>
            <button
              type="submit"
              disabled={!notes.trim() || isSubmittingReport}
              className="sf-btn-destructive flex-1 px-4 py-2 gap-2"
            >
              {isSubmittingReport ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Versturen...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Rapporteer
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReportUserModal;